import React from "react";
import { BranchFormProps } from "../types/branch";
import { removeEmojis, parseDate } from "../utils/validation";
import { useLocationOptions } from "../hooks/useLocationOptions";

export const useBranchFields = ({
  formData,
  doberror,
  error,
  refs,
  onFormChange,
  onDobChange,
  onPincodeChange,
  onBranchnameKeyPress,
  createKeyPressHandler,
  onKeyPress,
  isEdit = false,
  disabledFields = [],
}: BranchFormProps) => {
  const { countries, states, cities } = useLocationOptions(formData.country, formData.state);

  /** Disable logic */
  const isDisabled = (field: string) => disabledFields.includes(field) || (isEdit && field === "Branchname");

  /** Centralized handlers */
  const fieldHandlers: Record<string, (value: string) => void> = {
    Branchname: (value) => onFormChange({ name: removeEmojis(value) }),

    address1: (value) => onFormChange({ addressLine1: removeEmojis(value) }),

    street: (value) => onFormChange({ addressLine2: removeEmojis(value) }),

    branchopeningdate: (value) => {
      const clean = removeEmojis(value);

      onFormChange({ inputValue: clean });

      const parsed = parseDate(clean);

      refs?.setDobError?.(clean.length > 0 && !parsed);
      if (parsed) {
        onDobChange({
          target: { value: clean },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    },

    Pincode: (value) =>
      onPincodeChange({
        target: { value },
      } as React.ChangeEvent<HTMLInputElement>),

    Country: (value) => onFormChange({ country: value, state: "", city: "" }),

    State: (value) => onFormChange({ state: value, city: "" }),

    City: (value) => onFormChange({ city: value }),
  };

  /** Required label */
  const RequiredLabel: React.FC<{ label: string }> = ({ label }) => (
    <>
      {label} <span className="text-red-500">*</span>
    </>
  );

  const formatDateForInput = (date: any): string => {
    if (!date) return "";

    // Mongo style: { $date: "2023-11-20T00:00:00Z" }
    if (typeof date === "object" && date.$date) {
      const d = new Date(date.$date);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    }

    // JS Date object
    if (date instanceof Date) {
      return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    }

    // Already formatted string
    if (typeof date === "string") return date;

    return "";
  };

  const handleChange = (field: string, value: string) => {
    fieldHandlers[field]?.(value);
  };

  /** ALL FIELDS */
  const fields = [
    {
      id: "Branchname",
      label: <RequiredLabel label="Branch name" />,
      value: formData.name,
      onKeyPress: onBranchnameKeyPress,
      disabled: isDisabled("Branchname"),
      type: "text" as const,
      onChange: (val: string) => handleChange("Branchname", val),
    },

    {
      id: "branchopeningdate",
      label: "Branch opening date (dd-mm-yyyy)",
      value: formData.inputValue ?? formatDateForInput(formData.openingDate),
      refProp: refs.branchopeningdateref,
      onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" || e.key === "Delete") {
          return; 
        }
        if (!/[0-9-]/.test(e.key)) {
          e.preventDefault();
        }
      },
      error: !!doberror,
      helperText: doberror ? "Invalid date format, use dd-mm-yyyy" : undefined,
      disabled: isDisabled("branchopeningdate"),
      type: "text" as const,
      onChange: (val: string) => handleChange("branchopeningdate", val),
    },

    {
      id: "address1",
      label: "Flat / House no. / Building",
      value: formData.addressLine1,
      refProp: refs.addressline1,
      onKeyPress: createKeyPressHandler(refs.streetnames),
      disabled: isDisabled("address1"),
      type: "text" as const,
      onChange: (val: string) => handleChange("address1", val),
    },

    {
      id: "street",
      label: "Area / Street / Sector",
      value: formData.addressLine2 || "",
      refProp: refs.streetnames,
      onKeyPress: createKeyPressHandler(refs.Countryref),
      disabled: isDisabled("street"),
      type: "text" as const,
      onChange: (val: string) => handleChange("street", val),
    },

    {
      id: "Country",
      label: "Country",
      value: formData.country,
      refProp: refs.Countryref,
      onKeyPress: createKeyPressHandler(refs.Stateref),
      type: "select" as const,
      options: countries,
      grid: 4,
      disabled: isDisabled("Country"),
      onChange: (val: string) => handleChange("Country", val),
    },

    {
      id: "State",
      label: "State",
      value: formData.state,
      refProp: refs.Stateref,
      onKeyPress: createKeyPressHandler(refs.Cityref),
      type: "select" as const,
      options: states,
      grid: 4,
      disabled: !formData.country || isDisabled("State"),
      onChange: (val: string) => handleChange("State", val),
    },

    {
      id: "City",
      label: "City",
      value: formData.city,
      refProp: refs.Cityref,
      onKeyPress: createKeyPressHandler(refs.pincoderef),
      type: "select" as const,
      options: cities,
      grid: 4,
      disabled: !formData.state || isDisabled("City"),
      onChange: (val: string) => handleChange("City", val),
    },

    {
      id: "Pincode",
      label: "Pincode / Zipcode",
      value: formData.postalCode,
      refProp: refs.pincoderef,
      onKeyPress: onKeyPress || createKeyPressHandler(refs.pincoderef),
      error: !!error,
      helperText: error || undefined,
      disabled: isDisabled("Pincode"),
      type: "text" as const,
      onlyNumber: true, // 🔥 InputBox numeric restriction
      onChange: (val: string) => handleChange("Pincode", val),
    },
  ];

  return { fields, handleChange };
};
